/**
 * CreateInvoiceFromShipmentDialog — maak conceptfactuur vanuit zending.
 *
 * Sprint jun 2026: de voorstel-regels komen NIET meer uit een tweede
 * frontend-berekening maar uit `GET /admin/shipping-labels/{id}/invoice-prefill`
 * — dezelfde canonieke bron (`shipment_metrics`) die de backend gebruikt bij
 * het daadwerkelijk aanmaken. Zo kunnen kg, CBM en colli nooit meer afwijken
 * en worden colli en inhoud/items niet vermengd.
 *
 * De medewerker mag de voorgevulde regels controleren en aanpassen vóór het
 * aanmaken; daarna roept de dialoog
 * POST /admin/shipping-labels/{id}/create-invoice aan.
 *
 * Bij 409 (al gekoppeld) wordt de bestaande factuur-info getoond en
 * gebruiker kan direct naar Facturen navigeren.
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Receipt, Loader2, AlertCircle, CheckCircle2, ExternalLink, Trash2, Plus,
} from "lucide-react";

import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../ui/dialog";
import { adminApi, formatApiError } from "../../../context/AuthContext";

const num = (v) => {
  if (v === "" || v === null || v === undefined) return 0;
  const n = parseFloat(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : 0;
};

const fmt = (v, digits = 2) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return n.toFixed(digits).replace(/\.?0+$/, "") || "0";
};

const BASIS_LABELS = {
  volumetric_weight: "volumetrisch gewicht is leidend",
  gross_weight: "werkelijk gewicht is leidend",
};

export default function CreateInvoiceFromShipmentDialog({ shipment, onClose, onCreated }) {
  const [lines, setLines] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [prefillError, setPrefillError] = useState(null);
  const [loadingPrefill, setLoadingPrefill] = useState(true);
  const [busy, setBusy] = useState(false);
  const [existingInvoice, setExistingInvoice] = useState(null);
  const [createdInvoice, setCreatedInvoice] = useState(null);

  // Canonieke prefill — één bron met de backend.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingPrefill(true);
      try {
        const r = await adminApi.get(`/admin/shipping-labels/${shipment.id}/invoice-prefill`);
        if (cancelled) return;
        setLines(r.data?.lines || []);
        setMetrics(r.data?.metrics || null);
        setPrefillError(null);
      } catch (e) {
        if (cancelled) return;
        setPrefillError(formatApiError(e));
      } finally {
        if (!cancelled) setLoadingPrefill(false);
      }
    })();
    return () => { cancelled = true; };
  }, [shipment.id]);

  const totals = useMemo(() => {
    let subtotal = 0;
    let vat = 0;
    lines.forEach((ln) => {
      const sub = num(ln.unit_price) * num(ln.quantity);
      subtotal += sub;
      vat += (sub * num(ln.vat_percent)) / 100;
    });
    return {
      subtotal: subtotal.toFixed(2),
      vat: vat.toFixed(2),
      total: (subtotal + vat).toFixed(2),
    };
  }, [lines]);

  const updateLine = (idx, patch) => {
    setLines((ls) => ls.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  };
  const addLine = () => {
    setLines((ls) => [...ls, { description: "", quantity: 1, unit: "stuk", unit_price: 0, vat_percent: 21 }]);
  };
  const deleteLine = (idx) => {
    setLines((ls) => ls.filter((_, i) => i !== idx));
  };

  const submit = async () => {
    if (lines.length === 0) {
      toast.error("Minimaal 1 factuurregel vereist.");
      return;
    }
    if (lines.some((ln) => !ln.description?.trim())) {
      toast.error("Vul voor elke regel een omschrijving in.");
      return;
    }
    setBusy(true);
    setExistingInvoice(null);
    try {
      const r = await adminApi.post(`/admin/shipping-labels/${shipment.id}/create-invoice`, {
        lines: lines.map((ln) => ({
          description: ln.description.trim(),
          quantity: num(ln.quantity),
          unit: ln.unit || "stuk",
          unit_price: num(ln.unit_price),
          vat_percent: num(ln.vat_percent),
        })),
      });
      setCreatedInvoice(r.data);
      onCreated?.(r.data);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409 && typeof detail === "object" && detail?.existing_invoice_number) {
        setExistingInvoice(detail);
      } else if (typeof detail === "string") {
        toast.error(detail);
      } else {
        toast.error("Factuur aanmaken mislukt.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && !busy && onClose?.()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col" data-testid="create-invoice-dialog">
        <DialogHeader>
          <DialogTitle className="font-chivo font-black text-[#082c50] flex items-center gap-2">
            <Receipt className="w-5 h-5 text-[#0055FF]" /> Factuur maken vanuit zending
          </DialogTitle>
          <DialogDescription>
            Maak een conceptfactuur voor zending <span className="font-mono font-bold">{shipment.tracking_number}</span>.
            De factuur wordt opgeslagen in de bestaande factuurmodule en kan daar verder bewerkt en verstuurd worden.
          </DialogDescription>
        </DialogHeader>

        {createdInvoice ? (
          <div className="py-4 space-y-3" data-testid="create-invoice-success">
            <div className="text-center py-4">
              <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-2" />
              <h3 className="font-chivo font-bold text-lg text-[#082c50]">Factuur aangemaakt</h3>
              <div className="font-mono font-black text-2xl text-[#082c50] mt-1">{createdInvoice.invoice_number}</div>
              <div className="text-xs text-gray-500 mt-1">Status: Concept · Totaal {createdInvoice.currency} {createdInvoice.total_incl_vat}</div>
            </div>
            <a href={`/admin?section=facturen`} target="_self"
               className="block w-full text-center bg-[#0055FF] hover:bg-[#0044CC] text-white py-2 text-xs font-bold uppercase tracking-wide"
               data-testid="create-invoice-open-invoices">
              <ExternalLink className="inline w-3.5 h-3.5 mr-1" /> Naar factuurmodule
            </a>
          </div>
        ) : existingInvoice ? (
          <div className="py-4 space-y-3" data-testid="create-invoice-already-exists">
            <div className="bg-amber-50 border border-amber-200 p-4 rounded-md">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="font-bold text-amber-900">Factuur bestaat al</div>
                  <div className="text-sm text-amber-800 mt-1">{existingInvoice.message}</div>
                  <div className="font-mono font-bold text-amber-900 mt-2">{existingInvoice.existing_invoice_number}</div>
                </div>
              </div>
            </div>
            <a href={`/admin?section=facturen`} target="_self"
               className="block w-full text-center bg-[#0055FF] hover:bg-[#0044CC] text-white py-2 text-xs font-bold uppercase tracking-wide"
               data-testid="create-invoice-open-existing">
              <ExternalLink className="inline w-3.5 h-3.5 mr-1" /> Factuur openen in factuurmodule
            </a>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto py-2 space-y-3">
            <div className="bg-gray-50 border border-gray-200 p-3 rounded-md text-xs">
              <div className="font-bold text-[#082c50] mb-1">Klant-info voorgevuld</div>
              <div className="text-gray-700">
                {shipment.customer_id ? "Bestaande klant gekoppeld" : "Geen klantkoppeling — recipient-snapshot wordt gebruikt"}
                {" · "}
                {shipment.recipient?.name || "—"} · {shipment.recipient?.email || shipment.recipient?.phone || ""}
              </div>
            </div>

            {/* Canonieke zendingmetrieken — dezelfde bron als de backend */}
            {metrics && (
              <div className="bg-white border border-gray-200 p-3 rounded-md"
                   data-testid="create-invoice-metrics">
                <div className="text-[10px] uppercase font-bold tracking-wide text-gray-600 mb-2">
                  Zendinggegevens (canoniek)
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs font-mono">
                  <Metric label="Colli" value={metrics.piece_count} testId="create-invoice-metric-colli" />
                  <Metric label="Werkelijk gewicht" value={`${fmt(metrics.actual_weight_kg)} kg`} testId="create-invoice-metric-weight" />
                  <Metric label="CBM" value={`${fmt(metrics.cbm, 4)} m³`} testId="create-invoice-metric-cbm" />
                  <Metric label={`Volumetrisch (/${metrics.volumetric_divisor})`}
                          value={`${fmt(metrics.volumetric_weight_kg)} kg`}
                          testId="create-invoice-metric-volumetric" />
                  <Metric label="Belastbaar gewicht" value={`${fmt(metrics.chargeable_weight_kg)} kg`}
                          testId="create-invoice-metric-chargeable" />
                  <Metric label="Aangegeven waarde" value={fmt(metrics.declared_value)}
                          testId="create-invoice-metric-value" />
                </div>
                <div className="text-[10px] text-gray-500 mt-2">
                  {BASIS_LABELS[metrics.chargeable_weight_basis] || "—"}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <div className="text-[10px] uppercase font-bold tracking-wide text-gray-600">
                Factuurregels — voorgevuld, aanpasbaar vóór aanmaken
              </div>
              {loadingPrefill ? (
                <div className="flex items-center gap-2 text-xs text-gray-600 py-4"
                     data-testid="create-invoice-loading">
                  <Loader2 className="w-4 h-4 animate-spin" /> Voorstel-regels ophalen…
                </div>
              ) : prefillError ? (
                <div className="bg-red-50 border border-red-200 text-red-800 p-3 rounded-md text-xs"
                     data-testid="create-invoice-prefill-error">
                  {prefillError}
                </div>
              ) : lines.map((ln, idx) => (
                <div key={idx} className="bg-white border border-gray-200 p-3 rounded-md space-y-2"
                     data-testid={`create-invoice-line-${idx}`}>
                  <div className="flex items-start gap-2">
                    <div className="flex-1">
                      <Label className="text-[10px] uppercase font-bold tracking-wide text-gray-500">Omschrijving</Label>
                      <Textarea rows={2} value={ln.description}
                                onChange={(e) => updateLine(idx, { description: e.target.value })}
                                className="rounded-none text-xs"
                                data-testid={`create-invoice-line-${idx}-description`} />
                    </div>
                    {lines.length > 1 && (
                      <button type="button" onClick={() => deleteLine(idx)}
                              className="text-gray-400 hover:text-red-600 p-1 mt-5"
                              data-testid={`create-invoice-line-${idx}-delete`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <div>
                      <Label className="text-[10px] uppercase font-bold text-gray-500">Aantal</Label>
                      <Input type="number" step="0.1" value={ln.quantity}
                             onChange={(e) => updateLine(idx, { quantity: e.target.value })}
                             className="rounded-none h-9 text-xs"
                             data-testid={`create-invoice-line-${idx}-quantity`} />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase font-bold text-gray-500">Eenheid</Label>
                      <Input value={ln.unit} onChange={(e) => updateLine(idx, { unit: e.target.value })}
                             className="rounded-none h-9 text-xs"
                             data-testid={`create-invoice-line-${idx}-unit`} />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase font-bold text-gray-500">Prijs / stuk</Label>
                      <Input type="number" step="0.01" value={ln.unit_price}
                             onChange={(e) => updateLine(idx, { unit_price: e.target.value })}
                             className="rounded-none h-9 text-xs"
                             data-testid={`create-invoice-line-${idx}-price`} />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase font-bold text-gray-500">BTW%</Label>
                      <Input type="number" step="1" value={ln.vat_percent}
                             onChange={(e) => updateLine(idx, { vat_percent: e.target.value })}
                             className="rounded-none h-9 text-xs"
                             data-testid={`create-invoice-line-${idx}-vat`} />
                    </div>
                  </div>
                </div>
              ))}
              {!loadingPrefill && !prefillError && (
                <Button type="button" variant="outline" size="sm" onClick={addLine}
                        className="rounded-none text-xs h-9"
                        data-testid="create-invoice-add-line">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Regel toevoegen
                </Button>
              )}
            </div>

            <div className="bg-blue-50 border border-blue-200 p-3 rounded-md text-xs font-mono">
              <div className="flex justify-between"><span>Subtotaal excl. BTW:</span><span>EUR {totals.subtotal.replace(".", ",")}</span></div>
              <div className="flex justify-between"><span>BTW:</span><span>EUR {totals.vat.replace(".", ",")}</span></div>
              <div className="flex justify-between font-bold text-[#082c50] mt-1 pt-1 border-t border-blue-300">
                <span>Totaal incl. BTW:</span><span data-testid="create-invoice-total">EUR {totals.total.replace(".", ",")}</span>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          {createdInvoice || existingInvoice ? (
            <Button onClick={onClose} className="rounded-none text-xs h-9" data-testid="create-invoice-close">
              Sluiten
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={onClose} disabled={busy}
                      className="rounded-none text-xs h-9" data-testid="create-invoice-cancel">
                Annuleren
              </Button>
              <Button onClick={submit} disabled={busy || loadingPrefill || !!prefillError}
                      className="rounded-none text-xs h-9 bg-[#0055FF] text-white"
                      data-testid="create-invoice-submit">
                {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Receipt className="w-4 h-4 mr-1" />}
                Maak conceptfactuur
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Metric({ label, value, testId }) {
  return (
    <div>
      <div className="text-[9px] uppercase font-bold text-gray-500 tracking-wide">{label}</div>
      <div className="text-[#082c50] font-bold" data-testid={testId}>{value ?? "—"}</div>
    </div>
  );
}
